<template>
    <div>
        <!-- ** mainDialog -->
        <oss-dialog
            v-model="mainDialog"
            :title="title"
            @valid="upsert()"
            :validLoading="mainLoading"
            :validDisabled="readOnly || !valid || step === 'accClosing' || mainDialogValidDisabled"
            :validTitle="validTitle"
            :hide-valid="definitivelyClosed"
            prefixDataCy="AccountingPeriod-closeDialog"
            width="900px"
        >
            <!-- <v-card>
                <v-toolbar  color="primary" elevation="0">
                    <v-toolbar-title>{{ $t("settings.accountingPeriod") }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="() => { this.$emit('closeDialog') }">
                        <v-icon>$close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text> -->

            <template
                v-if="errorMessage && errorMessage.length"
                v-for="line in stringToArray(errorMessage)"
            >
                <h3 class="text-primary">{{ line }}</h3>
            </template>

            <!-- ** checkbox -->
            <v-row no-gutters v-if="showIsExceptionnalCheckBox">
                <v-col cols="auto">
                    <!-- existing closed accountingPeriod -->
                    <template
                        v-if="
                            accountingPeriod &&
                            (accountingPeriod.closed ||
                                newClosed ||
                                accountingPeriod.temporaryClosed)
                        "
                    >
                        <h3 class="pt-2">Exercice clôturé</h3>
                    </template>

                    <template
                        v-else-if="
                            accountingPeriod && accountingClosings && accountingClosings.length
                        "
                    >
                        <h3>Exercice ayant des situations intermédiaires</h3>
                    </template>

                    <!-- existing accountingPeriod with ACRs -->
                    <v-tooltip
                        v-else-if="
                            accountingPeriod &&
                            accountingPeriod.isDeletable &&
                            accountingPeriod.isDeletable.length
                        "
                        location="top"
                    >
                        <template v-slot:activator="{ props }">
                            <div v-bind="props" class="pt-2">
                                <v-checkbox
                                    dense
                                    v-model="isExceptionnalChange"
                                    label="Changement exceptionnel : attention, des éléments basés sur les dates d'exercice ont été enregistrés : si vous validez cette modification, veillez à les actualiser manuellement (liste en surbrillance)."
                                    hide-details="auto"
                                    class="font-weight-bold"
                                    size="small"
                                    @update:model-value="checkBoxFunction"
                                />
                            </div>
                        </template>
                        <span>
                            <AccountingPeriodIsDeletable
                                :isDeletable="accountingPeriod.isDeletable"
                            />
                        </span>
                    </v-tooltip>

                    <!-- new or open accountingPeriod -->
                    <v-checkbox
                        v-else
                        dense
                        v-model="isExceptionnalChange"
                        label="Changement exceptionnel"
                        hide-details="auto"
                        size="small"
                        @update:model-value="checkBoxFunction"
                    />
                </v-col>

                <v-col>
                    <v-tooltip
                        v-if="!id && accountingPeriods && accountingPeriods.length"
                        location="bottom"
                    >
                        <template v-slot:activator="{ props }">
                            <v-btn-icon v-bind="props" size="large" class="ml-2" icon="$oss-help" />
                        </template>

                        <template>
                            <p>Il est possible de créer un nouvel exercice, dans l'ordre :</p>
                            <p>
                                - juste après le dernier existant (après le
                                {{ formatDate(accountingPeriods[0].end) }}),
                            </p>

                            <template
                                v-if="
                                    missingAccountingPeriodLaps &&
                                    missingAccountingPeriodLaps.length
                                "
                            >
                                <p>
                                    - pour remplir les périodes intermédiaires pour lequelles
                                    n'existe pas d'exercice comptable (première disponible : du
                                    {{ formatDate(missingAccountingPeriodLaps[0].start) }} au
                                    {{ formatDate(missingAccountingPeriodLaps[0].end) }}).
                                </p>
                            </template>

                            <p>
                                - juste avant le premier existant (avant le
                                {{
                                    formatDate(
                                        accountingPeriods[accountingPeriods.length - 1].start,
                                    )
                                }}),
                            </p>
                        </template>
                    </v-tooltip>
                </v-col>
            </v-row>

            <v-tabs
                v-model="step"
                fixed-tabs
                style="
                    background-color: transparent !important;
                    border-radius: 16px;
                    overflow: hidden;
                "
            >
                <v-tab value="accPeriod" href="#accPeriod"> <strong>Exercice</strong></v-tab>
                <v-tab
                    value="accSituation"
                    href="#accSituation"
                    :disabled="
                        !id ||
                        (definitivelyClosed &&
                            accountingPeriod &&
                            !(
                                accountingPeriod.accountingSituations &&
                                accountingPeriod.accountingSituations.length
                            ))
                    "
                    data-cy="AccountingPeriod-situation"
                    ><strong>Situations</strong></v-tab
                >
                <v-tab
                    value="accClosing"
                    href="#accClosing"
                    :disabled="
                        !id ||
                        (definitivelyClosed && !(accountingClosings && accountingClosings.length))
                    "
                    data-cy="AccountingPeriod-cloture"
                    ><strong>Clôtures</strong></v-tab
                >
            </v-tabs>

            <v-tabs-window v-model="step">
                <!-- ** ACCOUNTING PERIOD STEP -->
                <v-window-item value="accPeriod">
                    <v-form v-model="valid">
                        <!-- ** existing accountingPeriod -->
                        <template v-if="accountingPeriod">
                            <!-- ** closed -->
                            <!-- ** or just closed, but not yet updated -->
                            <v-row
                                no-gutters
                                v-if="
                                    accountingPeriod.closed ||
                                    newClosed ||
                                    (accountingClosings && accountingClosings.length)
                                "
                            >
                                <v-col cols="6" class="pl-1 pt-2">
                                    <oss-date-picker-filled
                                        v-model="newStartDate"
                                        :textFieldReadOnly="false"
                                        :disabled="!startDateEditable"
                                        :min-date="minStartDate"
                                        @update:model-value="
                                            ($event) => $nextTick(() => onNewStartChanged($event))
                                        "
                                    />
                                </v-col>

                                <v-col cols="6" class="pl-1 pt-2">
                                    <oss-date-picker-filled
                                        v-model="newEndDate"
                                        :textFieldReadOnly="false"
                                        :disabled="!endDateEditable"
                                        :max-date="maxEndDate"
                                        @update:model-value="
                                            ($event) => $nextTick(() => onNewEndChanged($event))
                                        "
                                    />
                                </v-col>
                            </v-row>

                            <!-- ** open - tooltips - with accountingClosings or is not deletable -->
                            <template
                                v-else-if="
                                    accountingPeriod &&
                                    accountingPeriod.isDeletable &&
                                    accountingPeriod.isDeletable.length
                                "
                            >
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <div v-bind="props" class="pt-2">
                                            <v-row no-gutters>
                                                <v-col cols="6" class="pl-1 pt-2">
                                                    <oss-date-picker-filled
                                                        v-model="newStartDate"
                                                        :textFieldReadOnly="false"
                                                        :disabled="!startDateEditable"
                                                        :min-date="minStartDate"
                                                        @update:model-value="
                                                            ($event) =>
                                                                $nextTick(() =>
                                                                    onNewStartChanged($event),
                                                                )
                                                        "
                                                    />
                                                </v-col>

                                                <v-col cols="6" class="pl-1 pt-2">
                                                    <oss-date-picker-filled
                                                        v-model="newEndDate"
                                                        :textFieldReadOnly="false"
                                                        :disabled="!endDateEditable"
                                                        :max-date="maxEndDate"
                                                        @update:model-value="
                                                            ($event) =>
                                                                $nextTick(() =>
                                                                    onNewEndChanged($event),
                                                                )
                                                        "
                                                    />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </template>

                                    <span>
                                        <AccountingPeriodIsDeletable
                                            :isDeletable="accountingPeriod.isDeletable"
                                        />
                                    </span>
                                </v-tooltip>
                            </template>

                            <!-- ** open and deletable-->
                            <v-row no-gutters v-else>
                                <v-col cols="6" class="pl-1 pt-2">
                                    <oss-date-picker-filled
                                        v-model="newStartDate"
                                        :textFieldReadOnly="false"
                                        :readonly="readOnly"
                                        :disabled="!startDateEditable"
                                        :min-date="minStartDate"
                                        @update:model-value="
                                            ($event) => $nextTick(() => onNewStartChanged($event))
                                        "
                                    />
                                </v-col>

                                <v-col cols="6" class="pl-1 pt-2">
                                    <oss-date-picker-filled
                                        v-model="newEndDate"
                                        :textFieldReadOnly="false"
                                        :readonly="readOnly"
                                        :disabled="!endDateEditable"
                                        :max-date="maxEndDate"
                                        @update:model-value="
                                            ($event) => $nextTick(() => onNewEndChanged($event))
                                        "
                                    />
                                </v-col>
                            </v-row>
                        </template>

                        <!-- ** new - first period -->
                        <v-row
                            no-gutters
                            v-else-if="!accountingPeriods || !accountingPeriods.length"
                        >
                            <v-col cols="6" class="pl-1 pt-2">
                                <oss-date-picker-filled
                                    v-model="newStartDate"
                                    :textFieldReadOnly="false"
                                    :readonly="readOnly"
                                    @update:model-value="
                                        ($event) => $nextTick(() => onNewStartChanged($event))
                                    "
                                    prefixDataCy="AccountingPeriod-dateDebut"
                                />
                            </v-col>

                            <v-col cols="6" class="pl-1 pt-2">
                                <oss-date-picker-filled
                                    v-model="newEndDate"
                                    :textFieldReadOnly="false"
                                    :readonly="readOnly"
                                    @update:model-value="
                                        ($event) => $nextTick(() => onNewEndChanged($event))
                                    "
                                    prefixDataCy="AccountingPeriod-dateFin"
                                />
                            </v-col>
                        </v-row>

                        <!-- ** new -->
                        <v-row no-gutters v-else>
                            <v-col cols="6" class="pl-1 pt-2">
                                <oss-date-picker-filled
                                    v-model="newStartDate"
                                    :textFieldReadOnly="false"
                                    :readonly="readOnly"
                                    :disabled="!startDateEditable"
                                    :min-date="minStartDate"
                                    @update:model-value="
                                        ($event) => $nextTick(() => onNewStartChanged($event))
                                    "
                                    prefixDataCy="AccountingPeriod-dateDebut"
                                />
                            </v-col>

                            <v-col cols="6" class="pl-1 pt-2">
                                <oss-date-picker-filled
                                    v-model="newEndDate"
                                    :textFieldReadOnly="false"
                                    :readonly="readOnly"
                                    :disabled="!endDateEditable"
                                    :max-date="maxEndDate"
                                    @update:model-value="
                                        ($event) => $nextTick(() => onNewEndChanged($event))
                                    "
                                    prefixDataCy="AccountingPeriod-dateFin"
                                />
                            </v-col>
                        </v-row>

                        <!-- ** progress-linear -->
                        <v-row no-gutters v-if="stepData && stepData.action" class="my-2">
                            <h4 class="text-center">
                                {{ stepData.title !== "XXX" || "Clôture de l'exercice" }}
                            </h4>
                            <v-progress-linear
                                color="secondary"
                                height="10"
                                buffer-value="0"
                                stream
                                class="ml-2"
                                :model-value="stepData.progressValue"
                            />
                        </v-row>

                        <!-- ** carry forward and closure actions -->
                        <v-row no-gutters>
                            <v-spacer />

                            <!-- ** deleteCarryForward -->
                            <v-col class="d-flex justify-end">
                                <template v-if="carryForwardIsBlocked && deleteCarryForwardBtnText">
                                    <v-tooltip class="pt-4 px-4" location="bottom">
                                        <template v-slot:activator="{ props }">
                                            <div style="height: fit-content" v-bind="props">
                                                <v-btn
                                                    color="red"
                                                    rounded
                                                    elevation="0"
                                                    disabled
                                                    size="small"
                                                >
                                                    {{ deleteCarryForwardBtnText }}
                                                </v-btn>
                                            </div>
                                        </template>

                                        <template v-for="line of carryForwardTooltipMessage">
                                            <h4>
                                                {{ line }}
                                            </h4>
                                        </template>
                                    </v-tooltip>
                                </template>

                                <v-btn
                                    v-else-if="deleteCarryForwardBtnText"
                                    color="red"
                                    rounded
                                    elevation="0"
                                    @click="deleteCarryForward()"
                                    :loading="deleteCarryForwardLoading"
                                    size="small"
                                >
                                    {{ deleteCarryForwardBtnText }}
                                </v-btn>
                            </v-col>

                            <!-- ** computeCarryForward -->
                            <v-col class="d-flex justify-end pl-2">
                                <template
                                    v-if="carryForwardIsBlocked && computeCarryForwardBtnText"
                                >
                                    <v-tooltip class="pt-4 px-4" location="bottom">
                                        <!-- v-if="computeCarryForwardBtnText" -->
                                        <template v-slot:activator="{ props }">
                                            <div style="height: fit-content" v-bind="props">
                                                <v-btn rounded elevation="0" disabled size="small">
                                                    {{ computeCarryForwardBtnText }}
                                                </v-btn>
                                            </div>
                                        </template>

                                        <template v-for="line of carryForwardTooltipMessage">
                                            <h4>
                                                {{ line }}
                                            </h4>
                                        </template>
                                    </v-tooltip>
                                </template>

                                <v-btn
                                    v-else-if="computeCarryForwardBtnText"
                                    rounded
                                    elevation="0"
                                    @click="computeCarryForward()"
                                    :loading="computeCarryForwardLoading"
                                    :disabled="readOnly"
                                    size="small"
                                >
                                    {{ computeCarryForwardBtnText }}
                                </v-btn>
                            </v-col>

                            <v-col
                                class="d-flex justify-end pl-2"
                                v-if="!definitivelyClosed && !!id"
                            >
                                <v-btn
                                    rounded
                                    elevation="0"
                                    @click="openConfirmDialog()"
                                    :disabled="readOnly"
                                    data-cy="AccountingPeriod-clos1"
                                    size="small"
                                    >Clôturer l'exercice</v-btn
                                >
                            </v-col>
                            <v-col
                                class="d-flex justify-end pl-2"
                                v-if="definitivelyClosed && !!id"
                            >
                                <v-btn
                                    rounded
                                    elevation="0"
                                    @click="openReopenDialog()"
                                    :disabled="readOnly"
                                    data-cy="AccountingPeriod-open"
                                    size="small"
                                    >Réouvrir l'exercice</v-btn
                                >
                            </v-col>
                        </v-row>
                    </v-form>
                </v-window-item>

                <!-- ** ACCOUNTING SITUATION STEP -->
                <v-window-item value="accSituation">
                    <v-list
                        v-if="
                            accountingPeriod &&
                            accountingPeriod.accountingSituations &&
                            accountingPeriod.accountingSituations.length
                        "
                        single-line
                        class="pt-0"
                        density="compact"
                    >
                        <v-list-item
                            :ripple="false"
                            class="mb-2"
                            color="success"
                            v-for="(as, asi) in accountingPeriod.accountingSituations"
                            :key="asi"
                        >
                            <v-list-item-title class="body-1">
                                <v-row class="d-flex align-center" no-gutters>
                                    <v-col cols="9">
                                        <div class="d-flex align-center primary--text">
                                            <span
                                                :class="$vuetify.display.mobile ? 'ml-2' : ''"
                                                style="font-weight: bold"
                                            >
                                                {{ situationTitle(as) }}
                                            </span>
                                        </div>
                                    </v-col>
                                    <v-col cols="1" class="d-flex justify-center align-center">
                                        <v-btn-icon
                                            v-if="!as.closed"
                                            icon="$edit"
                                            @click="setAccountingSituationToUpsert(as)"
                                        />
                                    </v-col>
                                    <v-col cols="1" class="d-flex justify-center align-center">
                                        <v-btn-icon v-if="as.closed" icon="$lock_outline" />
                                    </v-col>
                                    <v-col cols="1" class="d-flex justify-center align-center">
                                        <template v-if="isAuthorized">
                                            <v-btn-trash
                                                v-if="
                                                    accountingSituationIsDeletable(
                                                        accountingPeriod,
                                                        as,
                                                    )
                                                "
                                                @click="openDeleteAccountingSituationDialog(as)"
                                                :data-cy="'AccountingSituation-trash-' + as.id"
                                            />

                                            <template v-else>
                                                <v-tooltip location="bottom">
                                                    <template v-slot:activator="{ props }">
                                                        <div>
                                                            <v-btn-trash disabled v-bind="props" />
                                                        </div>
                                                    </template>

                                                    <span>
                                                        <AccountingPeriodIsDeletable
                                                            :isDeletable="as.isDeletable"
                                                        />
                                                    </span>
                                                </v-tooltip>
                                            </template>
                                        </template>
                                    </v-col>
                                </v-row>
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>

                    <v-row no-gutters class="d-flex justify-center">
                        <v-col cols="4" class="pa-2 d-flex justify-center">
                            <v-btn-primary
                                block
                                :disabled="definitivelyClosed || readOnly"
                                @click="
                                    accountingSituationToUpsert =
                                        defaultAccountingSituationToUpsert();
                                    upsertAccountingSituationDialog = true;
                                "
                                data-cy="AccountingPeriod-addSituation"
                            >
                                Ajouter
                            </v-btn-primary>
                        </v-col>
                    </v-row>
                </v-window-item>

                <!-- ** ACCOUNTING CLOSING STEP -->
                <v-window-item value="accClosing">
                    <v-row no-gutters class="d-flex justify-center">
                        <v-col cols="12" class="pa-2">
                            <v-list
                                single-line
                                class="pt-0"
                                v-if="accountingClosings && accountingClosings.length"
                                density="compact"
                            >
                                <v-list-item
                                    :ripple="false"
                                    class="mb-2"
                                    color="success"
                                    v-for="(ac, aci) in accountingClosings"
                                    :key="aci"
                                >
                                    <v-list-item-title class="body-1">
                                        <v-row class="d-flex align-center" no-gutters>
                                            <v-col cols="9">
                                                <div class="d-flex align-center primary--text">
                                                    <span
                                                        :class="
                                                            $vuetify.display.mobile ? 'ml-2' : ''
                                                        "
                                                        style="font-weight: bold"
                                                    >
                                                        {{ closingTitle(ac) }}
                                                    </span>
                                                </div>
                                            </v-col>
                                            <v-col
                                                cols="1"
                                                class="d-flex justify-center align-center"
                                            >
                                                <!-- <v-btn-icon
                                                    icon="$edit"
                                                    @click="setACToUpsert(ac)"
                                                /> -->
                                            </v-col>
                                            <v-col
                                                cols="1"
                                                class="d-flex justify-center align-center"
                                            >
                                                <v-btn-icon icon="$lock_outline" />
                                            </v-col>
                                            <v-col
                                                cols="1"
                                                class="d-flex justify-center align-center"
                                            >
                                                <v-btn-trash
                                                    v-if="
                                                        accountingClosings &&
                                                        accountingClosings.length &&
                                                        aci === accountingClosings.length - 1 &&
                                                        !definitivelyClosed
                                                    "
                                                    @click="deleteAccountingClosing(ac.id)"
                                                    :loading="deleteAccountingClosingLoading"
                                                    :disabled="readOnly"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-col>
                        <v-col cols="4" class="pa-2 d-flex justify-center">
                            <v-btn-primary
                                block
                                :disabled="definitivelyClosed || readOnly"
                                @click="
                                    accountingClosingToUpsert = defaultAccountingClosingToUpsert();
                                    upsertAccountingClosingDialog = true;
                                "
                                data-cy="AccountingPeriod-addSituation"
                            >
                                Ajouter
                            </v-btn-primary>
                        </v-col>
                    </v-row>
                </v-window-item>
            </v-tabs-window>

            <!--</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="secondary"
                        rounded
                        elevation="0"
                        class="mr-5 mx-2"
                         size="small"
                        @click="valid()"
                        :loading="loading"
                    >{{id ? $t("shared.modify"):$t('shared.add')}}</v-btn>
                </v-card-actions>
            </v-card> -->
        </oss-dialog>

        <!-- ** closingDialog -->
        <oss-dialog
            v-model="closingDialog"
            title="Confirmation"
            @valid="closeAccountingPeriod"
            validTitle="Clôturer"
            :validLoading="closingLoading"
            prefixDataCy="AccountingPeriod-closePeriod"
            width="800px"
        >
            <v-row no-gutters v-if="(!stepData || !stepData.action) && !displayProgressLine">
                <v-col cols="12">
                    <h4 class="text-center">
                        &#xca;tes-vous sûr(e) de vouloir clôturer cet exercice comptable ?
                    </h4>
                </v-col>
            </v-row>
            <v-row no-gutters v-else class="my-2">
                <h4 class="text-center">{{ stepData.title }}</h4>
                <v-progress-linear
                    color="secondary"
                    height="10"
                    buffer-value="0"
                    stream
                    class="ml-2"
                    :model-value="stepData.progressValue"
                />
            </v-row>
        </oss-dialog>

        <!-- ** reopenDialog -->
        <oss-dialog
            v-model="reopenDialog"
            title="Confirmation"
            @valid="reopenAccountingPeriod"
            validTitle="Réouvrir l'exercice"
            :validLoading="reopenLoading"
            prefixDataCy="AccountingPeriod-valid"
            width="800px"
        >
            <v-row no-gutters>
                <v-col cols="12">
                    <h4 class="text-center">
                        &#xca;tes-vous sûr(e) de vouloir réouvrir cet exercice comptable ?
                    </h4>
                </v-col>
            </v-row>
        </oss-dialog>

        <!-- ** upsertAccountingClosingDialog -->
        <oss-dialog
            v-model="upsertAccountingClosingDialog"
            :title="accountingClosingToUpsert.id ? 'Modifier cette clôture' : 'Créer une clôture'"
            @valid="upsertAccountingClosing()"
            :loading="upsertAccountingClosingLoading"
            :validDisabled="!isUpsertClosingDialogValid || !!accountingClosingToUpsert.id"
            :validTitle="accountingClosingToUpsert.id ? 'Modifier' : 'Créer'"
            prefixDataCy="AccountingPeriod-create"
            width="800px"
        >
            <v-form v-model="isUpsertClosingDialogValid">
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-text-field-filled
                            v-model="accountingClosingToUpsert.name"
                            label="Nom"
                            :rules="[rules.notEmpty]"
                            outlined
                            data-cy="AccountingPeriod-name"
                            density="compact"
                        />
                        <v-select-filled
                            v-model="accountingClosingToUpsert.endDate"
                            :items="endDates"
                            item-value="value"
                            label="Date de fin de la situation"
                            :rules="[rules.notEmpty]"
                            elevation="0"
                            outlined
                            hide-details
                            :disabled="!!accountingClosingToUpsert.id"
                            data-cy="AccountingPeriod-dateEndCloture"
                            density="compact"
                        />
                        <!-- <oss-date-picker
                                v-model="accountingClosingToUpsert.endDate"
                                label="date de fin de la Situation"
                                :min-date="formatDateForOssDatePicker(conditionLastDate)"
                                :max-date="formatDateForOssDatePicker(newEndDate)"
                                hide-details
                                :disabled="accountingClosingToUpsert.id"
                            ></oss-date-picker> -->
                    </v-col>
                </v-row>
            </v-form>
        </oss-dialog>

        <!-- ** upsertAccountingSituationDialog -->
        <oss-dialog
            v-model="upsertAccountingSituationDialog"
            :title="
                accountingSituationToUpsert.id ? 'Modifier cette situation' : 'Créer une situation'
            "
            @valid="upsertAccountingSituation()"
            :loading="upsertAccountingSituationLoading"
            :validDisabled="!isUpsertSituationDialogValid"
            :validTitle="accountingSituationToUpsert.id ? 'Modifier' : 'Créer'"
            prefixDataCy="AccountingPeriod-create"
            width="800px"
        >
            <v-form v-model="isUpsertSituationDialogValid">
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-select-filled
                            v-model="accountingSituationToUpsert.endDate"
                            :items="endDates"
                            item-value="value"
                            label="Date de fin de la situation"
                            :rules="[rules.notEmpty]"
                            elevation="0"
                            outlined
                            hide-details
                            data-cy="AccountingPeriod-dateEndSituation"
                            density="compact"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </oss-dialog>

        <!-- ** deleteAccountingSituationDialog -->
        <oss-dialog
            v-if="selectedAccountingSituation"
            v-model="deleteAccountingSituationDialog"
            :error="true"
            :validTitle="`Supprimer la situation`"
            @valid="deleteAccountingSituation(selectedAccountingSituation)"
            :loading="deleteAccountingSituationLoading"
            prefixDataCy="ossFilterBar-delete"
            width="800px"
        >
            <h2 class="text-center">&#xca;tes-vous sûr(e) de vouloir supprimer la situation</h2>
            <h2 class="text-center">
                du
                {{ formatDate(selectedAccountingSituation.start) }} au
                {{ formatDate(selectedAccountingSituation.end) }} ?
            </h2>
        </oss-dialog>
        <!-- :title="'Suppression de la situation'" -->
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { Apollo } from "@/decorators";
import { cloneDeep, sortBy } from "lodash";

import UPSERT_ACCOUNTING_PERIOD from "../graphql/accountingPeriod/UpsertAccountingPeriod.gql";
import COMPUTE_CARRY_FORWARD from "../graphql/accountingPeriod/ComputeCarryForward.gql";
import DELETE_CARRY_FORWARD from "../graphql/accountingPeriod/DeleteCarryForward.gql";
import ACCOUNTING_CLOSINGS from "../graphql/accountingClosing/AccountingClosings.gql";
import DELETE_ACCOUNTING_CLOSING from "../graphql/accountingClosing/DeleteAccountingClosing.gql";
import UPSERT_ACCOUNTING_CLOSING from "../graphql/accountingClosing/UpsertAccountingClosing.gql";
import CLOSE_ACCOUNTING_PERIOD from "../graphql/accountingPeriod/CloseAccountingPeriod.gql";
import REOPEN_ACCOUNTING_PERIOD from "../graphql/accountingPeriod/ReopenAccountingPeriod.gql";
import DELETE_ACCOUNTING_SITUATION from "../graphql/accountingPeriod/DeleteAccountingPeriod.gql";

import UPSERT_ACCOUNTING_SITUATION from "../graphql/accountingPeriod/UpsertAccountingSituation.gql";

import {
    getAccountingPeriodEndMonths,
    getLastAccountingPeriod,
} from "../services/accountingPeriods";
import { getDBFormattedDate } from "../services/dateServices";
import { User } from "../store/store";
import { NotificationBase } from "./NotificationBase";
import { dateInsideAccountingPeriods } from "../services/accountingPeriods";
import { updateStoreUser } from "../services/userServices";
import AccountingPeriodIsDeletable from "./configuration/AccountingPeriodIsDeletable.vue";
import { Component, Vue, toNative, Model, Emit } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";
import { checkAccessRights } from "../services/accessRightServices";

@Component({
    components: {
        AccountingPeriodIsDeletable,
    },
    emits: ["upsert"],
})
export class AccountingPeriod extends NotificationBase {
    // @Prop() readonly value: boolean;
    @Prop({ default: "" }) readonly id: string;
    @Prop({ default: null }) readonly accountingPeriod: any;
    @Prop({ default: "" }) readonly companyId: string;
    @Prop({ default: null }) readonly carryForwardDiagnostic: any;
    @Prop({ default: null }) readonly prevAccountingPeriod: any;
    @Prop({ default: null }) readonly accountingPeriods: any;
    @Prop({ default: null }) readonly missingAccountingPeriodLaps: Array<any>;
    @Prop({ default: null }) readonly accountingPeriodsSuiteErrors: Array<any>;

    @Model() value;

    // ** DIALOGS
    mainDialog: boolean = false;
    closingDialog: boolean = false;
    reopenDialog: boolean = false;
    // ** no computeCarryForwardDialog
    // ** no deleteCarryForwardDialog
    upsertAccountingClosingDialog: boolean = false;
    // ** no deleteAccountingClosingDialog
    upsertAccountingSituationDialog: boolean = false;
    deleteAccountingSituationDialog: boolean = false;

    // ** LOADINGS
    mainLoading: boolean = false;
    closingLoading: boolean = false;
    reopenLoading: boolean = false;
    computeCarryForwardLoading: boolean = false;
    deleteCarryForwardLoading: boolean = false;
    upsertAccountingClosingLoading: boolean = false;
    deleteAccountingClosingLoading: boolean = false;
    upsertAccountingSituationLoading: boolean = false;
    deleteAccountingSituationLoading: boolean = false;

    step = "accPeriod";

    // ** DIALOGS VALID OR DISABLED
    valid: boolean = true;
    isUpsertClosingDialogValid = false;
    isUpsertSituationDialogValid = false;
    mainDialogValidDisabled: boolean = true;

    // ** SELECTED OR TOUPSERT
    accountingClosingToUpsert: any = this.defaultAccountingClosingToUpsert();
    accountingSituationToUpsert: any = this.defaultAccountingSituationToUpsert();
    selectedAccountingSituation: any = null;

    // ** CLOSED
    newClosed: boolean = false;
    temporaryClosed: boolean = false;
    // definitivelyClosed:boolean = false

    lastAccountingPeriod: any = null;

    isExceptionnalChange: boolean = false;
    // checkBoxLabel: string = null

    errorMessage = "";
    showIsExceptionnalCheckBox: boolean = false;
    showSuiteErrors: boolean = false;

    // ** DATES
    minStartDate: string = null;
    maxEndDate: string = null;
    newStartDate: string = "";
    newEndDate: string = "";
    newStartDateIsValid: boolean = false;
    newEndDateIsValid: boolean = false;
    startDateEditable: boolean = false;
    endDateEditable: boolean = false;

    get rules() {
        return {
            notEmpty: (value) => {
                return !!value || "A remplir";
            },
        };
    }

    get validTitle() {
        return this.id ? this.$t("shared.modify") : this.$t("shared.add");
    }

    get title() {
        let title = this.$t("settings.accountingPeriod") + " (";
        title += !this.id ? " nouveau " : this.newClosed ? "clôturé" : "ouvert";
        title += this.accountingClosingDate && ` le ${this.accountingClosingDate}`;
        title += ")";
        return title;
    }

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
        if (event) {
            this.mainDialog = false;
            this.closingDialog = false;
            this.reopenDialog = false;
        }
    }

    get isAuthorized() {
        const authorized = checkAccessRights(this.currentUser, "accountingPeriod", "write");
        return authorized;
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get readOnly() {
        return !this.currentUser.profile?.configuration?.accountingPeriod?.write;
    }

    get definitivelyClosed(): boolean {
        if (!this.accountingPeriod) {
            return false;
        }
        return this.accountingPeriod.closed || false;
    }

    get endDates() {
        const temporaryAccPeriod = { id: this.id, start: this.newStartDate, end: this.newEndDate };
        const result = getAccountingPeriodEndMonths(temporaryAccPeriod) || [];
        // if (result?.length) {
        //     if (!this.accountingSituationToUpsert.id) {
        //         result.pop();
        //         while (result[0]?.value <= this.conditionLastDate) {
        //             result.shift();
        //         }
        //     }
        // }
        return result;
    }

    get conditionLastDate() {
        if (this.accountingClosings?.length) {
            const resultDate = this.accountingClosings[this.accountingClosings.length - 1].endDate;
            return this.formatDateForOssDatePicker(resultDate);
        }
        return this.newStartDate;
    }

    async beforeMount() {
        if (this.accountingPeriod?.id) {
            this.onNewStartChanged(
                this.formatDateForOssDatePicker(this.accountingPeriod.start),
                false,
            );
            this.onNewEndChanged(this.formatDateForOssDatePicker(this.accountingPeriod.end), false);
        } else {
            this.newAccountingPeriodToCreate();
        }
        this.mainDialog = true;
        this.addListener("accountingPeriod");
        this.checkBoxFunction();
        this.newClosed = this.accountingPeriod?.closed || false;
    }

    beforeDestroy() {
        this.removeListener();
    }

    onRunning() {
        console.log("onRunning AP");
        this.computeCarryForwardLoading = true;
    }

    onEnded() {
        console.log("onEnded AP");
        this.computeCarryForwardLoading = false;
        this.closingLoading = false;
        this.closingDialog = false;
        if (this.$apollo?.queries?.accountingClosings) {
            this.$apollo.queries.accountingClosings?.refetch();
        }
        this.refresh();
        this.resetNotificationStepData();
        this.displayProgressLine = false;
    }

    // accountingSituations = []

    // A REVOIR ERIC & MONICA//

    // get accountingSituations() {
    //     debugger
    //     if (this.accountingPeriod?.accountingSituations?.length) {
    //         return this.accountingPeriod.accountingSituations.map(as => (
    //             {
    //                 accountingPeriod: {
    //                     start: this.accountingPeriod.start
    //                 },
    //                 id: as.id,
    //                 end: as.end
    //             }
    //         ))
    //     }
    // }

    setAccountingPeriod(accountingPeriod) {
        // if (this.accountingPeriods?.length && this.id.length) {
        //     this.accountingPeriod = this.accountingPeriods.find(ap => ap.id === this.id)
        // }
        this.onNewStartChanged(this.formatDateForOssDatePicker(accountingPeriod.start), false);
        this.onNewEndChanged(this.formatDateForOssDatePicker(accountingPeriod.end), false);
        this.checkBoxFunction();
        // if (accountingPeriod.closed && !this.temporaryClosed) {
        //     this.definitivelyClosed = true
        // } else {
        //      this.definitivelyClosed = false
        // }
        // this.temporaryClosed = accountingPeriod.temporaryClosed
        this.newClosed = accountingPeriod?.closed || false;
    }

    @Apollo({
        query: ACCOUNTING_CLOSINGS,
        variables() {
            return {
                accountingPeriod: this.id,
            };
        },
        skip() {
            return !this.id;
        },
        specificResult({ data, loading }: any) {
            if (!loading) {
                if (data?.accountingClosings) {
                    const accountingClosings = cloneDeep(data.accountingClosings);
                    if (accountingClosings?.length) {
                        accountingClosings.sort((a, b) => {
                            // ** ASC
                            if (a.endDate > b.endDate) return 1;
                            if (a.endDate < b.endDate) return -1;
                            return 0;
                        });
                    }

                    this.accountingClosings = accountingClosings;
                }
            }
        },
    })
    accountingClosings = [];

    get accountingClosingDate(): string {
        if (this.accountingClosings?.length === 1) {
            if (this.accountingClosings[0].createdAt) {
                return moment(this.accountingClosings[0].createdAt).format("DD/MM/YYYY");
            }
        }
        return "";
    }

    async deleteAccountingClosing(acID) {
        this.deleteAccountingClosingLoading = true;

        const result = await this.$apollo.mutate({
            mutation: DELETE_ACCOUNTING_CLOSING,
            variables: {
                id: acID,
            },
        });
        if (result) {
            this.deleteAccountingClosingLoading = false;
            this.$apollo.queries.accountingClosings.refetch();
            this.refresh();
        }

        // loading = false
    }

    async deleteAccountingSituation(as) {
        this.deleteAccountingSituationLoading = true;

        // const reopenedSituation = await this.$apollo.mutate({
        //     mutation: REOPEN_ACCOUNTING_PERIOD, // ** an accountingSituation is an accountingPeriod
        //     variables: {
        //         id: asID,
        //     },
        // });

        // if (reopenedSituation) {

        if (!as.closed) {
            const result = await this.$apollo.mutate({
                mutation: DELETE_ACCOUNTING_SITUATION,
                variables: {
                    id: as.id,
                },
            });

            if (result) {
                this.deleteAccountingSituationLoading = false;
                this.deleteAccountingSituationDialog = false;
                this.refresh();
            }
        } else {
            this.deleteAccountingSituationLoading = false;
            this.deleteAccountingSituationDialog = false;
        }
    }

    formatDate(date) {
        if (!date) return null;
        return moment(date).format("DD MMMM YYYY");
    }
    formatDateForOssDatePicker(date) {
        if (!date) return null;
        return moment(date).format("YYYY-MM-DD");
    }

    onNewStartChanged(date, callFunction = true) {
        if (!date) return;
        const validDate = moment(date).isValid();
        this.newStartDate = this.formatDateForOssDatePicker(date);

        if ((this.newStartDate.length === 10 || validDate) && callFunction) {
            this.newStartDateIsValid = true;
            this.checkBoxFunction();
        } else {
            this.newStartDateIsValid = false;
        }
    }

    onNewEndChanged(date, callFunction = true) {
        if (!date) return;
        const validDate = moment(date).isValid();
        this.newEndDate = this.formatDateForOssDatePicker(date);

        if (validDate && this.newEndDate.length === 10 && callFunction) {
            this.newEndDateIsValid = true;
            this.checkBoxFunction();
        } else {
            this.newEndDateIsValid = false;
        }
    }

    // checkClosingChoice() {
    // if (this.accountingPeriod?.id && !this.definitivelyClosed) {
    //     this.openConfirmDialog()
    //     this.$emit('close')
    // } else {
    // this.upsert()
    // }
    // }

    openConfirmDialog() {
        this.closingDialog = true;
        this.reopenDialog = false;
        this.mainDialog = false;
    }

    openReopenDialog() {
        this.reopenDialog = true;
        this.closingDialog = false;
        this.mainDialog = false;
    }

    async upsertAccountingSituation() {
        const start = getDBFormattedDate(this.newStartDate);
        const end = getDBFormattedDate(this.accountingSituationToUpsert.endDate);
        this.upsertAccountingSituationLoading = true;

        const result = await this.$apollo.mutate({
            mutation: UPSERT_ACCOUNTING_SITUATION,
            variables: {
                id: this.accountingSituationToUpsert.id || "",
                companyId: this.companyId,
                accountingPeriodId: this.id,
                start: start,
                end: end,
            },
        });
        if (result) {
            this.upsertAccountingSituationLoading = false;
            this.upsertAccountingSituationDialog = false;
            this.refresh();
        }
    }

    openDeleteAccountingSituationDialog(accountingSituation) {
        this.selectedAccountingSituation = accountingSituation;
        this.deleteAccountingSituationDialog = true;
    }

    async upsert() {
        const start = getDBFormattedDate(this.newStartDate);
        const end = getDBFormattedDate(this.newEndDate);

        this.mainLoading = true;
        const result = await this.$apollo.mutate({
            mutation: UPSERT_ACCOUNTING_PERIOD,
            variables: {
                id: this.id,
                companyId: this.companyId,
                start: start,
                end: end,
                // closed: this.newClosed,
                // temporaryClosed: this.temporaryClosed
            },
        });
        if (result) {
            this.mainLoading = false;
            this.mainDialog = false;
            this.refresh();
        }
    }

    @Emit("upsert")
    refresh() {
        this.mainLoading = false;
        this.mainDialog = false;
        updateStoreUser(this.$apollo, this.$store, this.currentUser.id);
    }

    async closeAccountingPeriod() {
        if (!this.newClosed) {
            this.closingLoading = true;

            const result = await this.$apollo.mutate({
                mutation: CLOSE_ACCOUNTING_PERIOD,
                variables: {
                    id: this.id,
                },
            });
            this.setNotificationStepData(this.id, this.onRunning, this.onEnded);
            this.displayProgressLine = true;
            // if (result.data?.closeAccountingPeriod) {
            //     this.setAccountingPeriod(result.data.closeAccountingPeriod)
            // }
            // if (result?.data?.closeAccountingPeriod) {
            //     this.closingLoading = false;
            //     this.closingDialog = false;
            //     this.refresh();
            // }
        }
        // this.closingDialog = false
    }

    displayProgressLine: boolean = false;

    async reopenAccountingPeriod() {
        if (this.newClosed) {
            this.reopenLoading = true;
            const result = await this.$apollo.mutate({
                mutation: REOPEN_ACCOUNTING_PERIOD,
                variables: {
                    id: this.id,
                },
            });
            if (result?.data?.reopenAccountingPeriod?.id) {
                this.reopenLoading = false;
                this.reopenDialog = false;
                this.refresh();
            } else {
                console.error("Error during reopen accountingPeriod:", result.errors);
            }
        } else {
            this.reopenLoading = false;
            this.reopenDialog = false;
        }
    }

    async computeCarryForward() {
        if (this.carryForwardDiagnostic?.prevApId) {
            this.computeCarryForwardLoading = true;
            const result = await this.$apollo.mutate({
                mutation: COMPUTE_CARRY_FORWARD,
                variables: {
                    accountingPeriodId: this.carryForwardDiagnostic.prevApId,
                    companyId: this.companyId,
                },
            });

            if (result) {
                this.setNotificationStepData(
                    this.carryForwardDiagnostic.prevApId,
                    this.onRunning,
                    this.onEnded,
                );
                this.computeCarryForwardLoading = false;
                // ** no computeCarryForwardDialog
                this.refresh();
            }
        }
    }

    async deleteCarryForward() {
        if (this.id) {
            this.deleteCarryForwardLoading = true;
            const result = await this.$apollo.mutate({
                mutation: DELETE_CARRY_FORWARD,
                variables: {
                    accountingPeriodId: this.id,
                    companyId: this.companyId,
                },
            });
            if (result) {
                this.deleteCarryForwardLoading = false;
                this.refresh();
            }
        }
    }

    @Watch("mainDialog")
    @Watch("closingDialog")
    @Watch("reopenDialog")
    closeDialogs() {
        if (!this.mainDialog && !this.closingDialog && !this.reopenDialog) {
            this.dialog = false;
        }
    }

    //
    //  accounting closing
    //

    async upsertAccountingClosing() {
        this.upsertAccountingClosingLoading = true;
        const endDate = getDBFormattedDate(this.accountingClosingToUpsert.endDate);

        const result = await this.$apollo.mutate({
            mutation: UPSERT_ACCOUNTING_CLOSING,
            variables: {
                id: this.accountingClosingToUpsert.id || "",
                name: this.accountingClosingToUpsert.name,
                accountingPeriodId: this.id,
                endDate,
            },
        });
        if (result) {
            if (!this.accountingClosingToUpsert.id && result?.data?.upsertAccountingClosing) {
                this.accountingClosingToUpsert.id = result.data.upsertAccountingClosing.id;
            }
            this.upsertAccountingClosingLoading = false;
            this.upsertAccountingClosingDialog = false;
            this.$apollo.queries.accountingClosings?.refetch();
            this.refresh();
        }
    }

    setACToUpsert(ac) {
        this.accountingClosingToUpsert = JSON.parse(JSON.stringify(ac));
        this.accountingClosingToUpsert.endDate = this.formatDateForOssDatePicker(
            this.accountingClosingToUpsert.endDate,
        );
        this.upsertAccountingClosingDialog = true;
    }

    setAccountingSituationToUpsert(as) {
        this.accountingSituationToUpsert = JSON.parse(JSON.stringify(as));
        this.accountingSituationToUpsert.end = this.formatDateForOssDatePicker(
            this.accountingSituationToUpsert.end,
        );
        this.upsertAccountingSituationDialog = true;
    }

    situationTitle(accountingSituation) {
        return (
            "Situation du " +
            moment(accountingSituation.start).format("DD/MM/YYYY") +
            " au " +
            moment(accountingSituation.end).format("DD/MM/YYYY")
        );
    }

    closingTitle(accountingClosing) {
        if (
            accountingClosing === this.accountingClosings[this.accountingClosings.length - 1] &&
            this.definitivelyClosed
        ) {
            return accountingClosing.name;
        }
        return accountingClosing.name + " - jusqu'au " + this.formatDate(accountingClosing.endDate);
    }

    defaultAccountingClosingToUpsert() {
        return { id: undefined, name: "", endDate: null };
    }

    defaultAccountingSituationToUpsert() {
        return { id: undefined, endDate: null };
    }

    newAccountingPeriodToCreate() {
        if (!this.id && this.accountingPeriods?.length) {
            this.lastAccountingPeriod = getLastAccountingPeriod(this.accountingPeriods);
            this.newStartDate = this.formatDateForOssDatePicker(
                moment(this.lastAccountingPeriod.end).add(1, "days"),
            );
            this.newEndDate = this.formatDateForOssDatePicker(
                moment(this.lastAccountingPeriod.end).add(1, "years").endOf("month"),
            );
            this.newClosed = false;
        }
    }

    deleteCarryForwardBtnText: string = "";
    computeCarryForwardBtnText: string = "";
    carryForwardTooltipMessage: Array<string> = [];

    get carryForwardIsBlocked() {
        this.deleteCarryForwardBtnText = "Supprimer le report à nouveau auto";
        this.computeCarryForwardBtnText = "Calculer le report à nouveau auto";
        this.carryForwardTooltipMessage.length = 0;

        if (!this.prevAccountingPeriod) {
            this.carryForwardTooltipMessage.push("Pas d'exercice comptable précédent");
            return true;
        }

        if (!this.prevAccountingPeriod?.closed) {
            this.carryForwardTooltipMessage.push(
                "L'exercice comptable précédent n'est pas clôturé",
            );
            return true;
        }

        if (!this.carryForwardDiagnostic) {
            this.carryForwardTooltipMessage.push("Pas de report à nouveau existant");
            return true;
        }

        if (
            this.accountingPeriod?.closed ||
            this.carryForwardDiagnostic.manualCarryForwards?.length ||
            this.readOnly
        ) {
            if (this.accountingPeriod?.closed) {
                this.carryForwardTooltipMessage.push("L'exercice comptable est clôturé");
            } else if (this.accountingPeriod?.accountingSituations?.length) {
                this.carryForwardTooltipMessage.push(
                    "Le report à nouveau est vérrouillé par une situation",
                );
            }

            if (this.readOnly) {
                this.carryForwardTooltipMessage.push("Vous êtes en mode lecture seule");
            }

            this.computeCarryForwardBtnText = "Re-calculer le report à nouveau auto";

            if (this.carryForwardDiagnostic.manualCarryForwards?.length) {
                if (
                    this.carryForwardDiagnostic?.carryForwardCount &&
                    !this.accountingPeriod?.closed
                ) {
                    // ** doublons : allow to delete auto carryForward
                    this.computeCarryForwardBtnText = "";
                    return false;
                } else {
                    this.carryForwardTooltipMessage.push(
                        "Il y a des écritures manuelles de report à nouveau : on peut les supprimer depuis 'Documents'",
                    );
                    this.deleteCarryForwardBtnText = "Supprimer le report à nouveau manuel";
                    this.computeCarryForwardBtnText = "";
                }
            }

            if (
                !this.carryForwardDiagnostic?.carryForwardCount &&
                !this.carryForwardDiagnostic.manualCarryForwards?.length
            ) {
                this.carryForwardTooltipMessage.push(
                    "Attention : aucun report à nouveau, ni manuel, ni auto",
                );
                this.deleteCarryForwardBtnText = "";
            }
            return true;
        }

        if (this.carryForwardDiagnostic?.carryForwardCount) {
            this.computeCarryForwardBtnText = "Ré-calculer le report à nouveau auto";
        } else {
            this.carryForwardTooltipMessage.push("Pas de report à nouveau");
            this.deleteCarryForwardBtnText = "";
        }
        return false;
    }

    checkBoxFunction() {
        this.showIsExceptionnalCheckBox = false;
        this.mainDialogValidDisabled = true;
        this.showSuiteErrors = false;
        this.startDateEditable = true;
        this.endDateEditable = true;
        this.errorMessage = "";

        if (this.newStartDateIsValid && this.newEndDateIsValid) {
            this.valid = true;
        } else {
            this.valid = false;
        }

        if (this.readOnly || !this.valid || this.step === "accClosing") {
            if (this.readOnly) {
                this.errorMessage = "Vous n'avez pas les droits pour modifier cette période";
            }

            if (!this.valid) {
                this.errorMessage = "Les dates de début et de fin doivent être renseignées";
            }
            return;
        }

        // ****************************************
        // ** incomplete dates
        // ****************************************
        if (!this.newStartDate || !this.newEndDate) {
            this.errorMessage = "Les dates de début et de fin doivent être renseignées";
            return;
        }

        // ****************************************
        // ** accountingPeriod longer than 2 years
        // ****************************************
        const maxEndDate = moment(this.newStartDate)
            .add(2, "years")
            .add(-1, "days")
            .format("YYYY-MM-DD");
        if (maxEndDate < this.newEndDate) {
            this.errorMessage = "La période ne peut pas dépasser 2 ans";
            return;
        }

        // ****************************************
        // ** start date after end date
        // ****************************************
        if (moment(this.newStartDate).isAfter(this.newEndDate)) {
            this.errorMessage = "La date de début est après la date de fin";
            return;
        }

        // // ****************************************
        // // ** with existing accountingPeriods
        // // ****************************************
        // // ** some date in existing accountingPeriods when create new accountingPeriod
        if (this.accountingPeriods?.length) {
            const impossibleStartDate = dateInsideAccountingPeriods(
                this.accountingPeriods,
                this.newStartDate,
                this.id,
            );
            const impossibleEndDate = dateInsideAccountingPeriods(
                this.accountingPeriods,
                this.newEndDate,
                this.id,
            );

            if (impossibleStartDate && impossibleEndDate) {
                this.errorMessage =
                    "Les dates de début et de fin sont comprises dans un exercice comptable existant";
                return true;
            } else if (impossibleStartDate) {
                this.errorMessage =
                    "La date de début est comprise dans un exercice comptable existant";
                return true;
            } else if (impossibleEndDate) {
                this.errorMessage =
                    "La date de fin est comprise dans un exercice comptable existant";
                return true;
            }
        }

        // ****************************************
        // ** new accounting period following each other, without missing dates between
        // ****************************************

        // ** EXEMPLE
        // ** this.accountingPeriods = [
        // **             {start: 01-01-2024, end: 31-12-2024},
        // **             {start: 01-01-2020, end: 31-12-2020}
        // **           ]
        // ** AND
        // ** this.firstPeriod 01-04-2019 31-12-2019

        // ** if accountingPeriods exists,
        // ** newStartDate, to be free, have to be
        // ** - 01-01-2025 (next period)
        // ** - 01-04-2019 (firstPeriod.start: company.startBusinessDate)

        // ** newEndDate, to be free, have to be the last period
        // ** - 31-12-2023 (first non existing period after 2024)
        // ** - 2019 the first period (after the last existing period)
        // ** - 31-12-2019 (firstPeriod.end: company.firstAccountingPeriodEnd )

        const dayAfterAP = this.accountingPeriod
            ? moment(this.accountingPeriod.end).add(1, "days").format("YYYY-MM-DD")
            : null;
        const apStartDate = this.accountingPeriod
            ? moment(this.accountingPeriod.start).format("YYYY-MM-DD")
            : null;
        const apEndDate = this.accountingPeriod
            ? moment(this.accountingPeriod.end).format("YYYY-MM-DD")
            : null;
        const apEndMinus2Years = this.accountingPeriod
            ? moment(this.accountingPeriod.end).add(-2, "years").add(1, "days").format("YYYY-MM-DD")
            : null;
        const apStartPlus2Years = this.accountingPeriod
            ? moment(this.accountingPeriod.start)
                  .add(2, "years")
                  .add(-1, "days")
                  .format("YYYY-MM-DD")
            : null;
        // *****************************************
        // ****** new ******************************
        if (!this.id) {
            // ** without existing accountingPeriods = first created period
            if (!this.accountingPeriods?.length) {
                this.showIsExceptionnalCheckBox = true;
                this.errorMessage =
                    "Le premier exercice comptable peut avoir une durée différente de 1 an (max 2 ans)";
                if (this.isExceptionnalChange) {
                    this.mainDialogValidDisabled = false;
                    return;
                }
                this.startDateEditable = false;
                this.endDateEditable = false;
                this.mainDialogValidDisabled = false;
                return;
            }

            // ****************************************
            // ** with existing accountingPeriods
            // ****************************************

            // ** some date in existing accountingPeriods when create new accountingPeriod
            const newStartDateInsideExistingPeriod = dateInsideAccountingPeriods(
                this.$store.state.accountingPeriod.accountingPeriods,
                this.newStartDate,
            );
            const newEndDateInsideExistingPeriod = dateInsideAccountingPeriods(
                this.$store.state.accountingPeriod.accountingPeriods,
                this.newEndDate,
            );
            if (
                !this.accountingPeriod?.id &&
                this.accountingPeriods.length &&
                (newStartDateInsideExistingPeriod || newEndDateInsideExistingPeriod)
            ) {
                this.errorMessage =
                    "Au moins une des dates est comprise dans un exercice comptable existant";
                return;
            }

            // ** new accounting period have to be just before or just after existing accountingPeriods
            // *****************************************
            // ** new accounting period is after more recent existing period
            const nextPeriodStart = moment(this.accountingPeriods[0].end)
                .add(1, "days")
                .format("YYYY-MM-DD");
            if (this.newStartDate >= nextPeriodStart) {
                // ** new accounting period is just after  more recent existing period
                if (this.newStartDate === nextPeriodStart) {
                    this.showIsExceptionnalCheckBox = true;
                    this.errorMessage =
                        "La nouvelle période, étant la dernière, peut avoir une durée différente de 1 an";
                    if (this.isExceptionnalChange) {
                        this.mainDialogValidDisabled = false;
                        return;
                    }
                    this.startDateEditable = false;
                    this.endDateEditable = false;
                    this.mainDialogValidDisabled = false;
                    return;
                } else {
                    // ** new accounting period is not just after more recent existing period
                    this.errorMessage =
                        "Le nouvel exercice comptable doit commencer le lendemain de l'exercice précédent";
                    return;
                }
            }

            if (this.missingAccountingPeriodLaps?.length) {
                // ** new previous period.end is first missing end date
                if (this.newEndDate === this.missingAccountingPeriodLaps[0].end) {
                    this.showIsExceptionnalCheckBox = true;
                    this.errorMessage =
                        "La nouvelle période peut être différente de 1 an, avoir une durée maximale de 2 ans et s'étaler sur les périodes vides";
                    for (const missingAP of this.missingAccountingPeriodLaps) {
                        this.errorMessage +=
                            "\n du " +
                            this.formatDate(missingAP.start) +
                            " au " +
                            this.formatDate(missingAP.end);
                    }
                    if (this.newEndDate === this.missingAccountingPeriodLaps[0].end) {
                        const maxStart = moment(this.missingAccountingPeriodLaps[0].end)
                            .add(-2, "years")
                            .add(1, "days")
                            .format("YYYY-MM-DD");
                        if (this.newStartDate >= this.missingAccountingPeriodLaps[0].start) {
                            if (this.newStartDate >= maxStart) {
                                this.mainDialogValidDisabled = false;
                                return;
                            } else {
                                this.errorMessage +=
                                    "\n Le début de la période doit être au plus tôt le " +
                                    maxStart;
                            }
                        } else {
                            this.errorMessage +=
                                "\n Le début de la période doit être au plus tôt le " +
                                this.missingAccountingPeriodLaps[0].start;
                        }
                    }
                    if (
                        this.newEndDate < this.missingAccountingPeriodLaps[0].end &&
                        this.newStartDate >= this.missingAccountingPeriodLaps[0].start
                    ) {
                        this.mainDialogValidDisabled = false;
                        return;
                    }
                    this.mainDialogValidDisabled = false;
                    return;
                } else {
                    // ** new previous period.end is not first missing end date
                    this.errorMessage =
                        "Les exercices comptable doivent être créés dans l'ordre chronologique, sans périodes manquantes : la première période manquante se termine le " +
                        this.missingAccountingPeriodLaps[0].end;
                    return;
                }
            }

            const endBeforeOldestPeriod = moment(
                this.accountingPeriods[this.accountingPeriods.length - 1].start,
            )
                .add(-1, "days")
                .format("YYYY-MM-DD");

            if (this.newEndDate === endBeforeOldestPeriod) {
                this.showIsExceptionnalCheckBox = true;
                this.errorMessage =
                    "La nouvelle période peut être différente de 1 an, avoir une durée maximale de 2 ans et se terminer le " +
                    this.formatDate(endBeforeOldestPeriod);
                if (this.isExceptionnalChange) {
                    this.startDateEditable = true;
                    this.endDateEditable = true;
                    this.mainDialogValidDisabled = false;
                    return;
                }
                this.startDateEditable = false;
                this.endDateEditable = false;
                this.mainDialogValidDisabled = false;
                return;
            } else {
                const startAfterMoreRecentPeriod = moment(this.accountingPeriods[0].end)
                    .add(1, "days")
                    .format("YYYY-MM-DD");
                this.errorMessage =
                    "Le nouvel exercice comptable doit commencer le " +
                    this.formatDate(startAfterMoreRecentPeriod) +
                    " ou se terminer le " +
                    this.formatDate(endBeforeOldestPeriod);
                this.startDateEditable = true;
                this.endDateEditable = true;
                this.mainDialogValidDisabled = true;
                return;
            }
        }

        // ** open existing accountingPeriod
        if (this.id?.length) {
            // // add bundles in isDeletable

            if (this.accountingPeriods.closed || this.accountingPeriods.temporaryClosed) {
                this.errorMessage = "La période est clôturée";
                this.startDateEditable = false;
                this.endDateEditable = false;
                this.mainDialogValidDisabled = true;
                return;
            }

            if (this.accountingClosings?.length) {
                this.errorMessage = "La période contient des situations intermédiaires";
                this.startDateEditable = false;
                this.endDateEditable = false;
                this.mainDialogValidDisabled = true;
                return;
            }

            if (!this.isExceptionnalChange) {
                this.startDateEditable = false;
                this.endDateEditable = false;
            }

            if (this.accountingPeriods.length === 1) {
                this.errorMessage =
                    "Le premier exercice comptable peut avoir une durée différente de 1 an (max 2 ans)";
                this.startDateEditable = true;
                this.endDateEditable = true;
                this.mainDialogValidDisabled = false;
                return;
            }

            const isMoreRecentPeriod = this.id === this.accountingPeriods[0].id;
            const isOldestPeriod =
                this.id === this.accountingPeriods[this.accountingPeriods.length - 1].id;
            // ** no missing laps
            if (!this.missingAccountingPeriodLaps?.length) {
                // ** more recent existing period
                // ** may change only start date
                // ** end date is not changed
                if (isMoreRecentPeriod) {
                    this.minStartDate = apStartDate;
                    if (this.newStartDate === this.minStartDate) {
                        this.startDateEditable = false;
                        this.showIsExceptionnalCheckBox = true;
                        // ** end date may be modified, max 2 years from end date
                        this.maxEndDate = moment(this.accountingPeriod.start)
                            .add(2, "years")
                            .add(-1, "days")
                            .format("YYYY-MM-DD");
                        this.errorMessage =
                            "- L'exercice le plus recent peut avoir une durée  différente de 1 an (max 2 ans)";
                        this.errorMessage +=
                            "\n- Dates limites : du " +
                            this.formatDate(this.minStartDate) +
                            " au " +
                            this.formatDate(this.maxEndDate);
                        if (this.newEndDate <= this.maxEndDate) {
                            this.mainDialogValidDisabled = false;
                            return;
                        } else {
                            // ** more recent existing period can not have a duration bigger as 2 years
                            this.errorMessage =
                                "La durée de l'exercice comptable le plus recent ne peut pas dépasser 2 ans";
                            return;
                        }
                    }
                }

                // ** older existing period
                // ** may change only start date
                // ** end date is not changed
                if (isOldestPeriod) {
                    this.maxEndDate = moment(
                        this.accountingPeriods[this.accountingPeriods.length - 1].end,
                    ).format("YYYY-MM-DD");
                    if (this.newEndDate === this.maxEndDate) {
                        this.endDateEditable = false;
                        this.showIsExceptionnalCheckBox = true;
                        // ** start date may be modified, max 2 years from end date
                        this.minStartDate = moment(
                            this.accountingPeriods[this.accountingPeriods.length - 1].end,
                        )
                            .add(-2, "years")
                            .add(1, "days")
                            .format("YYYY-MM-DD");
                        this.errorMessage =
                            "- L'exercice le plus ancien peut avoir une durée  différente de 1 an (max 2 ans)";
                        this.errorMessage +=
                            "\n- Dates limites : du " +
                            this.formatDate(this.minStartDate) +
                            " au " +
                            this.formatDate(this.maxEndDate);
                        if (this.newStartDate >= this.minStartDate) {
                            this.mainDialogValidDisabled = false;
                            return;
                        } else {
                            // ** older existing period can not have a duration bigger as 2 years
                            this.errorMessage =
                                "La durée de l'exercice comptable le plus ancien ne peut pas dépasser 2 ans";
                            return;
                        }
                    }
                }

                // ** not first nor older period and no missing laps
                // ** no dates changes allowed
                this.errorMessage =
                    "- L'exercice est entouré d'exercices existants : les dates de début et de fin ne peuvent pas être modifiées";
                this.isExceptionnalChange = false;
                this.showIsExceptionnalCheckBox = false;
                this.startDateEditable = false;
                this.endDateEditable = false;
                return;
            }

            // ** with missing laps
            // ** missing laps are to be filled from recent to older
            // ** changed start or end date may be in missing laps
            // ** max length of period is 2 years
            if (this.missingAccountingPeriodLaps?.length) {
                // ** older period
                // ** may change only start date
                this.minStartDate = apStartDate;
                this.maxEndDate = apEndDate;
                let haveMissingStart = false;
                let haveMissingEnd = false;

                if (isMoreRecentPeriod) {
                    this.errorMessage =
                        "L'exercice le plus recent peut avoir une durée différente de 1 an (max 2 ans)";
                } else if (isOldestPeriod) {
                    this.errorMessage =
                        "L'exercice le plus ancien peut avoir une durée différente de 1 an (max 2 ans)";
                } else {
                    this.errorMessage = "Cet exercice ";
                }

                if (isMoreRecentPeriod) {
                    this.maxEndDate = moment(this.accountingPeriod.start)
                        .add(2, "years")
                        .add(-1, "days")
                        .format("YYYY-MM-DD");
                    this.showIsExceptionnalCheckBox = true;
                    this.startDateEditable = false;
                    if (this.isExceptionnalChange) {
                        this.endDateEditable = true;
                    }
                } else {
                    const missingAfterFounded = this.missingAccountingPeriodLaps.find(
                        (lap) => lap.start === dayAfterAP,
                    );
                    if (missingAfterFounded) {
                        haveMissingEnd = true;
                        this.showIsExceptionnalCheckBox = true;
                        this.maxEndDate = missingAfterFounded.end;
                    } else {
                        this.endDateEditable = false;
                    }
                }

                if (isOldestPeriod) {
                    this.minStartDate = apEndMinus2Years;
                    this.showIsExceptionnalCheckBox = true;
                    this.endDateEditable = false;
                    if (this.isExceptionnalChange) {
                        this.startDateEditable = true;
                    }
                } else {
                    const dayBeforeAP = moment(this.accountingPeriod.start)
                        .add(-1, "days")
                        .format("YYYY-MM-DD");
                    const missingBeforeFounded = this.missingAccountingPeriodLaps.find(
                        (lap) => lap.end === dayBeforeAP,
                    );
                    if (missingBeforeFounded) {
                        haveMissingStart = true;
                        this.showIsExceptionnalCheckBox = true;
                        // this.endDateEditable = false
                        this.minStartDate = missingBeforeFounded.start;
                    } else {
                        this.startDateEditable = false;
                    }
                }

                if (haveMissingStart || haveMissingEnd) {
                    if (this.isExceptionnalChange) {
                        if (haveMissingStart) {
                            debugger;
                            this.minStartDate = apEndMinus2Years;
                            this.startDateEditable = true;
                        }
                        if (haveMissingEnd) {
                            this.maxEndDate = apStartPlus2Years;
                            this.endDateEditable = true;
                        }
                    }
                    this.errorMessage += "\n - Peut s'étaler sur les périodes vides ";
                } else if (!isMoreRecentPeriod && !isOldestPeriod) {
                    this.errorMessage +=
                        " est entouré d'exercices existants : les dates de début et de fin ne peuvent pas être modifiées. ";
                    this.showIsExceptionnalCheckBox = false;
                    return;
                }

                this.errorMessage +=
                    "\n- Dates limites : du " +
                    this.formatDate(this.minStartDate) +
                    " au " +
                    this.formatDate(this.maxEndDate);

                // ** max period length is 2 years is already tested
                const newDurationIsOk =
                    this.newStartDate >= this.minStartDate && this.newEndDate <= this.maxEndDate;

                if (
                    newDurationIsOk &&
                    (this.newStartDate != apStartDate || this.newEndDate != apEndDate)
                ) {
                    this.mainDialogValidDisabled = false;
                    return;
                } else {
                    return;
                }
            }
        }
        return;
    }

    accountingSituationIsDeletable(ap, currentAS) {
        if (this.definitivelyClosed) return false;

        if (currentAS.closed) return false;

        const sortedSituations = sortBy(ap.accountingSituations, "end");
        if (sortedSituations[sortedSituations.length - 1].id != currentAS.id) return false;

        return true;
    }

    // correctCarryForwardLoading:boolean = false

    // async correctCarryForward() {
    //     this.correctCarryForwardLoading = true
    //     if (this.id) {
    //         await this.$apollo.mutate({
    //             mutation: CORRECT_CARRY_FORWARD,
    //             variables: {
    //                 accountingPeriodId: this.id,
    //                 companyId: this.companyId,
    //             }
    //         })
    //     }
    //     this.correctCarryForwardLoading = false
    //     this.mainDialog = false
    //     this.refresh()
    // }

    stringToArray(string) {
        return string.split("\n");
    }
}

export default toNative(AccountingPeriod);
</script>
